export type MarketingFeature = {
  title: string;
  description: string;
  icon: string;
};

export type MarketingFaqItem = {
  question: string;
  answer: string;
};

export type MarketingPrinciple = {
  title: string;
  description: string;
};

export type MarketingWorkspaceFixture = {
  title: string;
  category: string;
  author: string;
  description: string;
  status?: string;
  href?: string;
  slug?: string;
  imageUrl?: string;
  imageAlt?: string;
  mediaPreview?: {
    kind: "agent-conversation";
    agentName: string;
    statusLabel: string;
    messages: {
      role: "user" | "assistant";
      text: string;
    }[];
  };
};

export type MarketingBlogFixture = {
  title: string;
  description: string;
  publishedAt: string;
  slug: string;
};

export type MarketingPricingTier = {
  slug: "spark" | "forge" | "blaze" | "inferno";
  title: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  annualLabel?: string;
  badge?: string;
  features: string[];
  mapsTo: "starter" | "pro";
};

export const marketingNavigation = [
  { title: "Guides", href: "/marketplace" },
  { title: "Notes", href: "/blog" },
  { title: "Features", href: "/features" },
  { title: "About", href: "/about" },
  { title: "Support", href: "/pricing" },
];

export const marketingFooterColumns = [
  {
    title: "Site",
    items: [
      { title: "Guides", href: "/marketplace" },
      { title: "Features", href: "/features" },
    ],
  },
  {
    title: "Resources",
    items: [{ title: "Notes", href: "/blog" }],
  },
  {
    title: "Company",
    items: [
      { title: "About", href: "/about" },
      { title: "Contact", href: "mailto:gateszhang92@gmail.com" },
    ],
  },
  {
    title: "Legal",
    items: [
      { title: "Privacy Policy", href: "/privacy-policy" },
      { title: "Terms of Service", href: "/terms-of-service" },
    ],
  },
];

export const homeFaq: MarketingFaqItem[] = [
  {
    question: "Is Death Stranding 2 Hub an official game site?",
    answer:
      "No. Death Stranding 2 Hub is an independent fan-run site. It collects spoiler-light launch notes, edition comparisons, and prep material, but it is not affiliated with Kojima Productions or PlayStation.",
  },
  {
    question: "What will I find here?",
    answer:
      "You will find release tracking, lore refreshers, spoiler-light guides, edition breakdowns, and curated notes designed to help you prepare for Death Stranding 2 without getting buried in noise.",
  },
  {
    question: "Will spoiler-heavy topics be separated?",
    answer:
      "Yes. The default editorial stance is spoiler-light. If a page goes deeper into story or late-game speculation, it should be clearly labeled before you open it.",
  },
  {
    question: "How often is the site updated?",
    answer:
      "Core pages are updated whenever launch facts, release timing, editions, or major gameplay signals change. Around major events and launch week, updates happen more frequently.",
  },
  {
    question: "Does this site sell the game itself?",
    answer:
      "No. Death Stranding 2 Hub is an editorial and companion site. It may summarize editions or point to official channels, but it is not the game storefront.",
  },
];

export const pricingFaq: MarketingFaqItem[] = [
  {
    question: "What is included in each plan?",
    answer:
      "Each plan adds more of the same core value: tighter release tracking, better supporter perks, and more frequent editorial summaries. Higher tiers focus on faster alerts and deeper launch-week coverage.",
  },
  {
    question: "Can I change plans later?",
    answer:
      "Yes. If plan changes are supported by the billing provider, you can switch as the site grows. If a direct change path is not available at checkout, contact support and we will help manually.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "There is no free trial at the moment, but eligible supporter plans still retain the published refund window so you can evaluate whether the coverage is useful for you.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit and debit cards through Stripe, including Visa, Mastercard, and American Express. All payments are processed securely.",
  },
  {
    question: "What do supporters actually get?",
    answer:
      "Supporters help fund ongoing coverage and can receive plan-specific extras such as cleaner digests, curated checklists, or faster release-week updates, depending on the tier.",
  },
];

export const homeHeroStats = [
  { value: "Confirmed first", label: "Release facts before rumor loops" },
  { value: "Spoiler-light", label: "Late-story topics stay clearly marked" },
  { value: "Built to revisit", label: "Guides and checklists worth reopening" },
];

export const homeFeatures: MarketingFeature[] = [
  {
    title: "Confirmed Release Timeline",
    description:
      "Keep official dates, platform notes, edition changes, and launch-week updates in one place instead of stitching them together from scattered posts.",
    icon: "server",
  },
  {
    title: "Edition Comparison That Starts With What Matters",
    description:
      "Compare content access, preorder bonuses, and collector tradeoffs before the cosmetic extras so buying decisions get easier, faster.",
    icon: "users",
  },
  {
    title: "Spoiler Policy You Can Predict",
    description:
      "The default stance is spoiler-light. If a page goes deeper into story or late speculation, that boundary should be explicit before the click.",
    icon: "download",
  },
  {
    title: "World and Systems Refreshers",
    description:
      "Catch up on the setting, returning context, and key systems without falling into late-game wiki territory.",
    icon: "message-square",
  },
  {
    title: "Launch-Day Checklist",
    description:
      "See what to sort out before launch day: platform choice, preloads, spoiler filters, and the first pages worth revisiting.",
    icon: "shield",
  },
  {
    title: "Signal Over Noise",
    description:
      "Separate confirmed changes from recycled speculation, repost chains, and discussion loops that do not help you prepare.",
    icon: "code",
  },
  {
    title: "Change Notes With Context",
    description:
      "When facts move, notes are written around what changed, why it matters, and what still remains unclear.",
    icon: "layers",
  },
  {
    title: "Reader-Supported, Not Feed-Driven",
    description:
      "The goal is a cleaner reference experience, not a noisy content treadmill built to imitate social feeds.",
    icon: "lock",
  },
];

export const homeWorkflowSteps = [
  {
    step: "01",
    title: "Start with what actually changed",
    description:
      "Open the latest note or timeline update first so you can see what is confirmed, what is still unclear, and what is just recycled chatter.",
    icon: "sparkles",
  },
  {
    step: "02",
    title: "Open the guide that matches your question",
    description:
      "Jump into editions, story refreshers, launch checklists, or FAQs depending on whether you are comparing purchases, catching up, or preparing for day one.",
    icon: "rocket",
  },
  {
    step: "03",
    title: "Come back only when there is a reason",
    description:
      "The site is meant to be worth reopening: concise when nothing important moved, specific when something did.",
    icon: "message-circle-more",
  },
];

export const homeEditorialStandards: MarketingPrinciple[] = [
  {
    title: "Confirmed before amplified",
    description:
      "Core pages should start with what is official, well sourced, or clearly labeled as still uncertain.",
  },
  {
    title: "Spoilers labeled before depth",
    description:
      "Readers should know the spoiler boundary before they commit to a page, not halfway through the paragraph.",
  },
  {
    title: "Useful enough to revisit",
    description:
      "Guides, timelines, and checklists should help on the second read, not just feel polished on the first one.",
  },
];

export const pricingTiers: MarketingPricingTier[] = [
  {
    slug: "spark",
    title: "Scout",
    description:
      "Light support tier for readers who want a cleaner place to track the game before launch.",
    monthlyPrice: "$29.90",
    annualPrice: "$24.90",
    annualLabel: "Annual Save 17%",
    features: [
      "Weekly signal digest",
      "Edition comparison access",
      "Spoiler-light prep notes",
      "Launch checklist pack",
      "Standard support",
    ],
    mapsTo: "starter",
  },
  {
    slug: "forge",
    title: "Porter",
    description:
      "Balanced tier for readers who want better summaries and more structured launch prep coverage.",
    monthlyPrice: "$59.90",
    annualPrice: "$49.90",
    annualLabel: "Annual Save 17%",
    features: [
      "Everything in Scout",
      "More frequent update notes",
      "Collector edition checklist",
      "Priority roundup access",
      "Standard support",
    ],
    mapsTo: "starter",
  },
  {
    slug: "blaze",
    title: "Bridge",
    description:
      "Launch-week tier for readers who want faster alerts and deeper breakdowns when details move.",
    monthlyPrice: "$109.00",
    annualPrice: "$89.00",
    annualLabel: "Annual Save 18%",
    badge: "Most Popular",
    features: [
      "Everything in Porter",
      "Launch-week alert priority",
      "Deep-dive editorial bundles",
      "Unlimited archive access",
      "1 supporter Q&A batch/month",
      "Priority support",
    ],
    mapsTo: "pro",
  },
  {
    slug: "inferno",
    title: "Chiral",
    description:
      "Highest-support tier for readers who want the fastest updates and the most complete companion coverage.",
    monthlyPrice: "$239.00",
    annualPrice: "$199.00",
    annualLabel: "Annual Save 16%",
    features: [
      "Everything in Bridge",
      "Highest-priority launch alerts",
      "Full archive and supporter extras",
      "Dedicated recap delivery",
      "Quarterly supporter roadmap note",
      "Dedicated support",
    ],
    mapsTo: "pro",
  },
];

export const featurePageSections: MarketingFeature[] = [
  {
    title: "Spoiler-Light Launch Tracking",
    description:
      "The site is organized to surface confirmed launch details, edition changes, and meaningful updates without forcing you through spoiler-heavy threads.",
    icon: "server",
  },
  {
    title: "Edition and Purchase Prep",
    description:
      "Compare editions, preorder bonuses, and buying considerations in one place instead of stitching them together across storefront listings and rumor accounts.",
    icon: "download",
  },
  {
    title: "Lore Refreshers",
    description:
      "Refresh the world, themes, and returning systems without having to reread long wikis or watch hour-long recap videos.",
    icon: "download",
  },
  {
    title: "Checklist-First Reading",
    description:
      "Important pages prioritize practical decisions first: what changed, what to prep, and what to ignore until launch.",
    icon: "message-square",
  },
  {
    title: "Structured Archive",
    description:
      "Archive pages keep notes, comparisons, and release updates grouped into a structure that is easier to revisit than social timelines.",
    icon: "shield",
  },
  {
    title: "Reader-Supported Coverage",
    description:
      "Supporter plans fund ongoing coverage while keeping the site focused on useful launch prep instead of ad-heavy churn.",
    icon: "users",
  },
  {
    title: "Fast Search and Retrieval",
    description:
      "The site is built so you can jump back to a specific note or checklist quickly when you need it again later.",
    icon: "code",
  },
  {
    title: "Versioned Notes",
    description:
      "Editorial notes are updated deliberately so major changes are easier to track over time.",
    icon: "layers",
  },
  {
    title: "Clear Support and Access",
    description:
      "Account, billing, and supporter access flows stay explicit so the site remains understandable and easy to manage.",
    icon: "lock",
  },
];

export const aboutPagePricingTable = [
  ["Scout", "$29.90", "$299", "Weekly", "Core", "Light", "Digest"],
  ["Porter", "$59.90", "$599", "Frequent", "Extended", "Medium", "Guide Pack"],
  ["Bridge", "$109", "$1,090", "Fast", "Priority", "High", "Launch Week"],
  ["Chiral", "$239", "$2,390", "Fastest", "Dedicated", "Highest", "Full Bundle"],
];

export const blogFixtures: MarketingBlogFixture[] = [
  {
    title: "Death Stranding 2 Release Timeline: What Is Confirmed So Far",
    description:
      "A running note that separates official release facts, schedule changes, and still-unconfirmed assumptions.",
    publishedAt: "March 23, 2026",
    slug: "death-stranding-2-release-timeline",
  },
  {
    title: "Death Stranding 2 Editions Explained: What Changes and What Does Not",
    description:
      "A practical edition guide that starts with content access and purchase differences instead of marketing fluff.",
    publishedAt: "March 22, 2026",
    slug: "death-stranding-2-editions-explained",
  },
  {
    title: "Spoiler-Light Death Stranding Refresher Before DS2",
    description:
      "A compact refresher on the world, major themes, and safe context worth remembering before starting the sequel.",
    publishedAt: "March 21, 2026",
    slug: "spoiler-light-death-stranding-refresher",
  },
  {
    title: "Launch Week Checklist for Death Stranding 2",
    description:
      "A checklist-first note covering what to decide, what to preload, and what to mute before the week gets noisy.",
    publishedAt: "March 20, 2026",
    slug: "death-stranding-2-launch-week-checklist",
  },
];

export const homeWorkspaceFixtures: MarketingWorkspaceFixture[] = [
  {
    title: "Release Signal Board",
    category: "Launch Tracker",
    author: "Editorial Desk",
    description:
      "A concise running board that separates confirmed Death Stranding 2 updates from recycled rumor noise.",
    status: "Updated when official facts move",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "release-signal-board",
      statusLabel: "updated",
      messages: [
        {
          role: "user",
          text: "What actually changed in the latest Death Stranding 2 update?",
        },
        {
          role: "assistant",
          text: "Filtering out rumor posts and cross-checking only the official signals that affect release prep, editions, or launch timing...",
        },
        {
          role: "assistant",
          text: "✓ Summary ready. One confirmed update matters, three rumor loops do not, and the launch checklist has been updated.",
        },
      ],
    },
  },
  {
    title: "Edition Breakdown",
    category: "Buying Guide",
    author: "Launch Notes",
    description:
      "A side-by-side guide to standard, deluxe, and collector options focused on the differences that affect the purchase decision.",
    status: "Reference guide",
    imageUrl: "/images/deathstranding2/wallpapers/edition-breakdown.jpg",
    imageAlt:
      "Collector-style expedition gear and premium boxed equipment arranged on a dark industrial table under moody gray light",
  },
  {
    title: "Lore Refresher",
    category: "Story Prep",
    author: "Story Desk",
    description:
      "A spoiler-light recap of the world, themes, and safe returning context worth remembering before launch.",
    status: "Spoiler-light by default",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "lore-refresher",
      statusLabel: "ready",
      messages: [
        {
          role: "user",
          text: "Give me the context I need before Death Stranding 2, but avoid endgame spoilers.",
        },
        {
          role: "assistant",
          text: "Focusing on world-state, major themes, and safe returning-context notes while explicitly skipping late-story reveals...",
        },
        {
          role: "assistant",
          text: "✓ Refresher prepared. You can get back into the setting quickly without crossing spoiler boundaries.",
        },
      ],
    },
  },
  {
    title: "Launch Checklist",
    category: "Launch Prep",
    author: "Prep Room",
    description:
      "A practical launch-week checklist covering platform choice, preloads, spoiler filters, and what to review first.",
    status: "Checklist",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "launch-checklist",
      statusLabel: "ready",
      messages: [
        {
          role: "user",
          text: "What should I sort out before launch day so I am not scrambling later?",
        },
        {
          role: "assistant",
          text: "Organizing the prep into platform, access, spoiler settings, and first-session priorities...",
        },
        {
          role: "assistant",
          text: "✓ Checklist complete. You now have a short launch-day sequence instead of ten scattered tabs.",
        },
      ],
    },
  },
  {
    title: "Community Watchlist",
    category: "Signals",
    author: "Roundup Desk",
    description:
      "A curated short list of community sources worth checking when the signal-to-noise ratio gets worse.",
    status: "Curated sources",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "community-watchlist",
      statusLabel: "updated",
      messages: [
        {
          role: "user",
          text: "Which community sources are worth watching without drowning in recycled posts?",
        },
        {
          role: "assistant",
          text: "Filtering for signal quality, consistency, and whether a source adds anything beyond reposting official material...",
        },
        {
          role: "assistant",
          text: "✓ Watchlist updated. A short list remains worth checking; the rest can be ignored unless something breaks.",
        },
      ],
    },
  },
  {
    title: "Release-Week Briefing",
    category: "Roundup",
    author: "Briefing Desk",
    description:
      "A concise briefing that compresses the latest meaningful Death Stranding 2 updates into a short read.",
    status: "Weekly note",
    imageUrl: "/images/deathstranding2/wallpapers/release-week-briefing.jpg",
    imageAlt:
      "A lone courier with stacked cargo facing a windswept black sand coastline beneath a cold gray sky",
  },
];

export const marketplaceCategories = [
  { label: "Release Timeline", icon: "zap" },
  { label: "Edition Guides", icon: "chart-column" },
  { label: "Story Refreshers", icon: "wrench" },
  { label: "Launch Checklists", icon: "globe" },
  { label: "Community Signals", icon: "bot" },
  { label: "Reader FAQs", icon: "test-tube" },
];
